/**
 * @file WmsTransfersPage.tsx
 * @description Реестр перемещений (phase19.23 этап B; phase19.31 зона
 * «Склад» — переведён целиком на Commora).
 *
 * Заголовок «Перемещения» в верхней панели, главное действие одно —
 * «+ Перемещение» (документ заводится диалогом; на телефоне и планшете
 * кнопка — в общей нижней полосе каркаса). Фильтры (`TransfersFilters`) →
 * `ResponsiveList` → «Показать ещё». Фильтры, период и страницы считает
 * сервер (`useWmsDocumentsList`).
 *
 * Период по умолчанию — 30 дней, поэтому у пустой выдачи два разных повода:
 * «за период ничего нет» (предлагаем расширить до «всё время» одной кнопкой)
 * и «документов не заводили».
 */

import { ArrowLeftRight, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import type { ListEmptySpec } from "../../../components/ui/responsive-list-types";
import { transferColumns } from "../../../components/wms/registry/transfer-columns";
import { WmsDocumentsListBody } from "../../../components/wms/registry/WmsDocumentsListBody";
import { TransferCreateDialog } from "../../../components/wms/transfers/TransferCreateDialog";
import { TransfersFilters } from "../../../components/wms/transfers/TransfersFilters";
import { useWmsDocumentsList } from "../../../components/wms/useWmsDocumentsList";
import { wmsPeriodLabel } from "../../../lib/wms-period";

export function WmsTransfersPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const basePath = `/shop/${slug}/wms/transfers`;
  // Основание-приёмка ссылается на свой документ — путь знает страница.
  const columns = transferColumns(`/shop/${slug}/wms/receipts`);
  const list = useWmsDocumentsList("transfers", "Не удалось загрузить перемещения. Проверьте связь и попробуйте ещё раз.");
  // `?create=1` — вход из «⋯ → Новое перемещение» на обзоре склада: своей
  // страницы у создания нет, документ заводится диалогом этого реестра.
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(searchParams.get("create") === "1");
  const limitedByPeriod = list.period !== "all";
  const showAllTime = () => list.setPeriod("all");

  /** Закрыть диалог и убрать `create` из адреса — иначе он откроется снова. */
  function closeCreate() {
    setCreateOpen(false);
    if (!searchParams.has("create")) return;
    const params = new URLSearchParams(searchParams);
    params.delete("create");
    setSearchParams(params, { replace: true });
  }

  // Пусто без фильтров: «за период ничего» — кнопка расширить период;
  // иначе — документов ещё не заводили.
  const empty: ListEmptySpec = limitedByPeriod
    ? {
        icon: Search,
        title: `За ${wmsPeriodLabel(list.period)} перемещений нет`,
        description: "Документы старше этого периода скрыты — посмотрите за всё время.",
        action: (
          <Button variant="outline" onClick={showAllTime}>
            Показать за всё время
          </Button>
        ),
      }
    : {
        icon: ArrowLeftRight,
        title: "Перемещений пока нет",
        description: "Перемещением раскладывают товар по ячейкам и переносят партии между складами.",
      };

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Перемещения"
        subtitle="Раскладка товара по ячейкам и переезд партий между складами"
        action={
          <Button variant="brand" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Перемещение
          </Button>
        }
      />

      <TransfersFilters
        search={list.search}
        onSearchChange={list.setSearch}
        source={list.source}
        onSourceChange={list.setSource}
        period={list.period}
        onPeriodChange={list.setPeriod}
        status={list.status}
        onStatusChange={list.setStatus}
        warehouseId={list.warehouseId}
        onWarehouseChange={list.setWarehouseId}
        warehouses={list.warehouses}
      />

      <WmsDocumentsListBody
        list={list}
        columns={columns}
        rowKey={(t) => t.id}
        rowHref={(t) => `${basePath}/${t.id}`}
        rowLabel={(t) => `Перемещение ${t.transferNumber}`}
        ariaLabel="Реестр перемещений"
        filtered={list.filtering}
        empty={empty}
        filteredEmpty={{
          icon: Search,
          title: "Под эти условия перемещений нет",
          description: limitedByPeriod
            ? `Измените поиск, статус или склад — сейчас показан период «${wmsPeriodLabel(list.period)}».`
            : "Измените поиск, статус или склад.",
          onReset: list.reset,
        }}
        allShownText="Показаны все перемещения"
      />

      <TransferCreateDialog
        open={createOpen}
        onClose={closeCreate}
        onCreated={(created) => {
          closeCreate();
          navigate(`${basePath}/${created.id}`);
        }}
      />
    </div>
  );
}
